import * as Sentry from '@sentry/react';
import { Severity } from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';

const FILTERED = '[Filtered]';
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const IP_V4_PATTERN =
  /\b(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\b/g;
// 圧縮表記（::）を持つ IPv6、または完全表記（8 グループ）のみに一致させ、
// "22:00:00" のような時刻表記を IPv6 と誤認しないようにする。
const IP_V6_PATTERN =
  /\b(?:(?:[0-9a-f]{1,4}:){7}[0-9a-f]{1,4}|(?:[0-9a-f]{1,4}(?::[0-9a-f]{1,4})*)?::(?:[0-9a-f]{1,4}(?::[0-9a-f]{1,4})*)?)\b/gi;
// 国内表記（090-1234-5678 等）と国際表記（+81 90-1234-5678）の両方を拾う。
const PHONE_PATTERN =
  /(?:\+81[-\s]?\d{1,3}[-\s]?\d{1,4}[-\s]?\d{3,4}|\(?\b0\d{1,4}\)?[-\s]?\d{1,4}[-\s]?\d{3,4})\b/g;
const QUERY_PARAM_PATTERN = /([?&])([^=\s&]+)=([^&\s]*)/g;
// 末尾がパス区切り・拡張子・引用符・空白・文字列末のいずれでも検出できるようにする
// （`/users/123.json` のような format 拡張子付きのケースを含む）。
const NUMERIC_PATH_SEGMENT_PATTERN = /\/\d+(?=[/."'\s]|$)/g;
const UUID_PATTERN_SOURCE = [
  '[0-9a-f]{8}',
  '[0-9a-f]{4}',
  '[1-5][0-9a-f]{3}',
  '[89ab][0-9a-f]{3}',
  '[0-9a-f]{12}',
].join('-');
const UUID_PATH_SEGMENT_PATTERN = new RegExp(
  `/${UUID_PATTERN_SOURCE}(?=[/."'\\s]|$)`,
  'gi',
);
const ANONYMIZED_USER_ID_PATTERN = /^(user|admin)_[0-9a-f]{32}$/i;

const SENSITIVE_KEY_PATTERNS = [
  /passw/i,
  /email/i,
  /phone/i,
  /tel/i,
  /first_name/i,
  /last_name/i,
  /name_kana/i,
  /\bname$/i,
  /dob/i,
  /birth/i,
  /zip/i,
  /postal/i,
  /address/i,
  /building/i,
  /bank/i,
  /branch/i,
  /account/i,
  /certificate/i,
  /token/i,
  /secret/i,
  /authorization/i,
  /cookie/i,
  /_key/i,
  /crypt/i,
  /salt/i,
  /otp/i,
  /ssn/i,
  /^ip(_address)?$/i,
  /remote_ip/i,
  /reason/i,
  /memo/i,
  /image/i,
  /file/i,
  /^(user|admin|operator|participant|applicant)_?id$/i,
];

// Sentry SDK がスタックフレームに自動で付与するキーで、PII ではなく
// デバッグに必須の診断情報のため、SENSITIVE_KEY_PATTERNS に部分一致しても
// マスク対象から除外する。
const NON_SENSITIVE_KEY_EXCLUSIONS = new Set([
  'filename',
  'abs_path',
  'module',
  'function',
  'raw_function',
  'package',
  'lineno',
  'colno',
  'context_line',
  'pre_context',
  'post_context',
  'in_app',
  'platform',
  'culture',
]);

// OS / ランタイム / ブラウザ等、SDK が自動付与する contexts のサブハッシュ。
// ここに含まれる name/version 等はキー名ベースでは redact せず、
// 値の内容だけを文字列スクラブする。
const SAFE_CONTEXT_KEYS = new Set([
  'os',
  'runtime',
  'browser',
  'device',
  'app',
  'gpu',
]);

type UnknownRecord = Record<string, unknown>;
type SentryEvent = Parameters<NonNullable<BrowserOptions['beforeSend']>>[0];

export const scrubSentryEvent = (event: SentryEvent): SentryEvent => {
  if (event.user) {
    const user = event.user as UnknownRecord;
    delete user.email;
    delete user.ip_address;
    delete user.username;
    // 匿名化パターンに一致する String 以外（number 等を含む）はすべて削除する。
    if (
      !(typeof user.id === 'string' && ANONYMIZED_USER_ID_PATTERN.test(user.id))
    ) {
      delete user.id;
    }
  }

  if (event.message) {
    event.message = scrubString(event.message);
  }

  if (event.request) {
    event.request = scrubRequest(
      event.request as UnknownRecord,
    ) as SentryEvent['request'];
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra) as SentryEvent['extra'];
  }

  if (event.contexts) {
    event.contexts = scrubContexts(
      event.contexts as UnknownRecord,
    ) as SentryEvent['contexts'];
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = scrubValue(
      event.breadcrumbs,
    ) as SentryEvent['breadcrumbs'];
  }

  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = scrubString(exception.value);
    }
  });

  return event;
};

// beforeSend は @sentry/react v6 ではトランザクションイベントに適用されない
// （エラーイベントにのみ適用される）。BrowserTracing の pageload/navigation
// トランザクションにも同じスクラブを適用するため、beforeSend とは別に
// グローバルイベントプロセッサとして登録する。Sentry.init() の前後どちらで
// 呼んでも安全（プロセッサはクライアントとは独立したグローバルレジストリに
// 登録される）。
export const registerGlobalEventScrubbing = (): void => {
  Sentry.addGlobalEventProcessor(scrubSentryEvent);
};

// 422 応答時の Sentry 通知。core/shared 両方の ApiService から共通で使う。
// level を明示し、domain + path でグルーピングすることで、
// 全エンドポイントの 422 が単一 issue に集約されるのを防ぐ。
export const reportApiErrorToSentry = (
  error: {
    response?: { status?: number };
    config?: { method?: string; url?: string };
  },
  domain: string,
): void => {
  Sentry.withScope((scope) => {
    const status = error.response?.status;
    const method = error.config?.method?.toUpperCase();
    const path = safeSentryPath(error.config?.url);

    scope.setLevel(Severity.Error);
    scope.setTag('api.domain', domain);
    if (status) scope.setTag('http.status_code', String(status));
    if (method) scope.setTag('http.method', method);
    if (path) scope.setTag('http.path', path);
    scope.setFingerprint(
      ['api-error', domain, method, path].filter(Boolean) as string[],
    );

    Sentry.captureMessage('API request failed with reportable status');
  });
};

export const safeSentryPath = (url?: string): string | undefined => {
  if (!url) return undefined;

  try {
    const baseUrl =
      typeof window === 'undefined'
        ? 'https://otetsutabi.invalid'
        : window.location.origin;
    const parsedUrl = new URL(url, baseUrl);
    return scrubString(maskPath(parsedUrl.pathname));
  } catch (_e) {
    return scrubString(maskPath(url.split('?')[0]));
  }
};

const scrubRequest = (request: UnknownRecord): UnknownRecord => {
  const sanitized: UnknownRecord = { ...request };

  if (typeof sanitized.url === 'string') {
    sanitized.url = safeSentryUrl(sanitized.url);
  }

  sanitized.query_string = FILTERED;
  sanitized.data = FILTERED;
  sanitized.cookies = FILTERED;
  // 辞書型のまま維持し、キー単位でマスクする（Authorization 等は redact、
  // User-Agent 等の非機微ヘッダは残す）。文字列で丸ごと上書きすると
  // Sentry のイベントスキーマ（headers は Object 型）に反するため。
  sanitized.headers = isPlainObject(sanitized.headers)
    ? scrubValue(sanitized.headers, 'headers')
    : FILTERED;

  return sanitized;
};

// SAFE_CONTEXT_KEYS 配下（os/runtime/browser 等）はキー名によるフィルタを
// 行わず、値の内容スクラブのみを適用する（os.name などが redact されるのを防ぐ）。
const scrubContexts = (contexts: UnknownRecord): UnknownRecord =>
  Object.entries(contexts).reduce<UnknownRecord>((sanitized, [key, value]) => {
    sanitized[key] = SAFE_CONTEXT_KEYS.has(key)
      ? scrubValueWithoutKeyFilter(value)
      : scrubValue(value, key);
    return sanitized;
  }, {});

const scrubValueWithoutKeyFilter = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => scrubValueWithoutKeyFilter(item));
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<UnknownRecord>(
      (sanitized, [childKey, childValue]) => {
        sanitized[childKey] = scrubValueWithoutKeyFilter(childValue);
        return sanitized;
      },
      {},
    );
  }

  if (typeof value === 'string') {
    return scrubString(value);
  }

  return scrubValue(value);
};

const scrubValue = (value: unknown, key?: string): unknown => {
  if (key && isSensitiveKey(key)) return FILTERED;

  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item));
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<UnknownRecord>(
      (sanitized, [childKey, childValue]) => {
        sanitized[childKey] = scrubValue(childValue, childKey);
        return sanitized;
      },
      {},
    );
  }

  if (typeof value === 'string') {
    return scrubString(value);
  }

  if (
    value === null ||
    value === undefined ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  ) {
    return value;
  }

  // Error/Map/Set 等、Plain Object・Array・プリミティブのいずれでもない値は
  // 内容を検査せずコンストラクタ名のみを残す（Error.message 等に含まれる
  // PII が未スクラブのまま送信されるのを防ぐ、Ruby 版 scrub_value の
  // else 分岐 `value.class.name` と揃えた安全側フォールバック）。
  return (
    (value as { constructor?: { name?: string } }).constructor?.name ??
    typeof value
  );
};

const scrubString = (value: string): string =>
  scrubQueryString(
    maskPath(
      value
        .replace(EMAIL_PATTERN, FILTERED)
        .replace(IP_V4_PATTERN, FILTERED)
        .replace(IP_V6_PATTERN, FILTERED)
        .replace(PHONE_PATTERN, FILTERED),
    ),
  );

const scrubQueryString = (value: string): string =>
  value.replace(QUERY_PARAM_PATTERN, (_matched, separator, key, rawValue) =>
    isSensitiveKey(key)
      ? `${separator}${key}=${FILTERED}`
      : `${separator}${key}=${rawValue}`,
  );

const safeSentryUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url, 'https://otetsutabi.invalid');
    parsedUrl.search = '';
    parsedUrl.hash = '';
    parsedUrl.pathname = maskPath(parsedUrl.pathname);
    return scrubString(
      parsedUrl.toString().replace('https://otetsutabi.invalid', ''),
    );
  } catch (_e) {
    return scrubString(maskPath(url.split('?')[0]));
  }
};

const maskPath = (path: string): string =>
  path
    .replace(NUMERIC_PATH_SEGMENT_PATTERN, '/:id')
    .replace(UUID_PATH_SEGMENT_PATTERN, '/:uuid');

const isSensitiveKey = (key: string): boolean => {
  if (NON_SENSITIVE_KEY_EXCLUSIONS.has(key)) return false;
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
};

const isPlainObject = (value: unknown): value is UnknownRecord =>
  value !== null &&
  typeof value === 'object' &&
  Object.prototype.toString.call(value) === '[object Object]';
